import { inject, Injectable } from '@angular/core';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import { type DatasetRow, type WorkflowSchema } from '@blaze-shared/schema-domain';
import { type ComputeWorkflowNode } from '@blaze-shared/workflow-domain';

/**
 * Adds a column to each input row, holding the node's expression evaluated in that row's context.
 *
 * With no input there is no row to extend, so the node yields a bare object holding the value. A
 * name already taken — by the schema or by the data — is written as `Computed_<name>` rather than
 * overwriting what is there.
 */
@Injectable({ providedIn: 'root' })
export class ComputeActionService {
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  compute(
    node: ComputeWorkflowNode,
    nodeInput: DatasetRow[] | null | undefined,
    ctx: RuntimeContext,
    schema: WorkflowSchema,
  ): any {
    const col = node.name;
    let rows = nodeInput;
    if (rows && !Array.isArray(rows)) {
      rows = [rows];
    } else if (Array.isArray(rows) && rows.length === 0) {
      return [];
    } else if (!rows && node.value) {
      const context = this.iss.updateRuntimeContext(ctx, rows);
      return { [col]: this.iss.eval(node.value, context) };
    } else if (!rows) {
      rows = [];
    }

    const isNameTaken =
      !!findColumn(schema.columns, col) ||
      rows.some((row) => Object.keys(row).some((rowColumn) => rowColumn === col));
    const rawColName = isNameTaken ? `Computed_${col}` : col;
    const colName = rawColName
      ? this.schemaService.getColumnName(rawColName) || rawColName
      : rawColName;

    return rows.map((row) => ({
      ...row,
      [colName]: this.iss.eval(node.value, this.iss.updateRuntimeContext(ctx, row)),
    }));
  }
}
